import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, Select, MenuItem,
  FormControl, InputLabel,
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import CancelIcon       from '@mui/icons-material/Cancel';
import EventIcon        from '@mui/icons-material/Event';
import VideocamIcon     from '@mui/icons-material/Videocam';
import EditNoteIcon     from '@mui/icons-material/EditNote';
import { getMisSesionesOrientador, gestionarSesion } from '../services/authApi';

const ESTADO_CONFIG = {
  PROGRAMADA: { label:'Programada',  color:'#e65100', bg:'#fff3e0' },
  AGENDADA:   { label:'Agendada',    color:'#1565c0', bg:'#e3f2fd' },
  REALIZADA:  { label:'Realizada',   color:'#2e7d32', bg:'#e8f5e9' },
  CANCELADA:  { label:'Cancelada',   color:'#888',    bg:'#f5f5f5' },
  NO_ASISTIO: { label:'No asistió',  color:'#c62828', bg:'#ffebee' },
};

const cotOffset = -5 * 60;
function formatFecha(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + cotOffset);
  return d.toLocaleString('es-CO', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

export default function VistaOrientador({ onToast }) {
  const [sesiones,    setSesiones]   = useState([]);
  const [cargando,    setCargando]   = useState(true);
  const [error,       setError]      = useState(null);
  const [dlgGestion,  setDlgGestion] = useState({ open:false, sesion:null });
  const [accion,      setAccion]     = useState('FINALIZAR');
  const [notas,       setNotas]      = useState('');
  const [nuevaFecha,  setNuevaFecha] = useState('');
  const [guardando,   setGuardando]  = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await getMisSesionesOrientador();
      setSesiones(Array.isArray(data) ? data : []);
    } catch {
      setError('No se pudieron cargar las sesiones.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirGestion = (s) => {
    setDlgGestion({ open:true, sesion:s });
    setAccion(s.estado === 'REALIZADA' ? 'APLAZAR' : 'FINALIZAR');
    setNotas('');
    setNuevaFecha('');
  };

  const confirmarGestion = async () => {
    if (!dlgGestion.sesion) return;
    if (accion === 'APLAZAR' && !nuevaFecha) {
      onToast('Indica la nueva fecha para aplazar', 'error'); return;
    }
    setGuardando(true);
    try {
      await gestionarSesion(dlgGestion.sesion.sesionID, {
        accion,
        nuevaFecha: accion === 'APLAZAR' ? new Date(nuevaFecha).toISOString() : null,
        notas,
      });
      onToast(`Sesión ${accion.toLowerCase()}da correctamente`);
      setDlgGestion({ open:false, sesion:null });
      cargar();
    } catch (e) {
      onToast(e?.data?.mensaje || 'Error al gestionar la sesión', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const sesionesFiltradas = filtroEstado
    ? sesiones.filter(s => s.estado === filtroEstado)
    : sesiones;

  const pendientes = sesiones.filter(s => ['PROGRAMADA','AGENDADA'].includes(s.estado)).length;

  return (
    <Box sx={{ p:3 }}>
      {/* Encabezado */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color:'#1a3a1a' }}>
            Mis sesiones de orientación
          </Typography>
          {pendientes > 0 && (
            <Chip label={`${pendientes} pendiente${pendientes>1?'s':''}`} size="small"
              sx={{ background:'#fff3e0', color:'#e65100', fontWeight:700 }}/>
          )}
        </Box>
        <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
          <FormControl size="small" sx={{ minWidth:140 }}>
            <InputLabel>Estado</InputLabel>
            <Select value={filtroEstado} label="Estado"
              onChange={e => setFiltroEstado(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(ESTADO_CONFIG).map(([k,v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" onClick={cargar} sx={{ color:'#2e7d32' }}>
            Actualizar
          </Button>
        </Box>
      </Box>

      {/* Estados */}
      {cargando && (
        <Box sx={{ display:'flex', justifyContent:'center', py:6 }}>
          <CircularProgress sx={{ color:'#2e7d32' }}/>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

      {!cargando && sesionesFiltradas.length === 0 && (
        <Box sx={{ textAlign:'center', py:6, color:'#bbb' }}>
          <EventIcon sx={{ fontSize:48, mb:1, opacity:0.4 }}/>
          <Typography>No tienes sesiones {filtroEstado ? 'con ese estado' : 'asignadas'}</Typography>
        </Box>
      )}

      {/* Lista de sesiones */}
      {!cargando && sesionesFiltradas.map(s => {
        const cfg = ESTADO_CONFIG[s.estado] || ESTADO_CONFIG.PROGRAMADA;
        const puedeGestionar = ['PROGRAMADA','AGENDADA'].includes(s.estado);
        return (
          <Box key={s.sesionID} sx={{
            p:2, mb:1.5, borderRadius:2, border:'1px solid',
            borderLeft:'4px solid',
            borderColor: cfg.color,
            background: '#fafafa',
          }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <Box sx={{ flex:1 }}>
                {/* Nombre afiliado y estado */}
                <Box sx={{ display:'flex', gap:1, alignItems:'center', mb:0.5 }}>
                  <Typography fontWeight={700} fontSize="0.9rem">
                    {s.nombreAfiliado}
                  </Typography>
                  <Chip label={cfg.label} size="small"
                    sx={{ height:18, fontSize:'0.62rem', fontWeight:700,
                      background:cfg.bg, color:cfg.color }}/>
                </Box>

                {/* Detalles */}
                <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
                  <Typography variant="caption" sx={{ color:'#555' }}>
                    📅 {formatFecha(s.fechaSesion)}
                  </Typography>
                  {s.duracionMin && (
                    <Typography variant="caption" sx={{ color:'#555' }}>
                      ⏱ {s.duracionMin} min
                    </Typography>
                  )}
                  {s.medioSesion && (
                    <Typography variant="caption" sx={{ color:'#555' }}>
                      💬 {s.medioSesion}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color:'#888' }}>
                    📧 {s.emailAfiliado}
                  </Typography>
                  {s.telefonoAfiliado && (
                    <Typography variant="caption" sx={{ color:'#888' }}>
                      📱 {s.telefonoAfiliado}
                    </Typography>
                  )}
                </Box>

                {/* URL Meet */}
                {s.urlGrabacion && (
                  <Box sx={{ mt:0.5 }}>
                    <Button size="small" variant="text" startIcon={<VideocamIcon fontSize="small"/>}
                      onClick={() => window.open(s.urlGrabacion, '_blank')}
                      sx={{ p:0, fontSize:'0.72rem', color:'#1565c0' }}>
                      Unirse al Meet
                    </Button>
                  </Box>
                )}

                {/* Notas */}
                {s.notasAdmin && (
                  <Typography variant="caption" sx={{ color:'#888', display:'block', mt:0.3, fontStyle:'italic' }}>
                    📝 {s.notasAdmin}
                  </Typography>
                )}
              </Box>

              {/* Acciones */}
              {puedeGestionar && (
                <Tooltip title="Gestionar sesión (aplazar / cancelar / finalizar)">
                  <IconButton size="small" onClick={() => abrirGestion(s)}
                    sx={{ color:'#1a4d2e', border:'1px solid #c8e6c9', borderRadius:1.5 }}>
                    <EditNoteIcon fontSize="small"/>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      })}

      {/* Diálogo de gestión */}
      <Dialog open={dlgGestion.open} onClose={() => setDlgGestion({ open:false, sesion:null })}
        maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:3 } }}>
        <DialogTitle sx={{
          background:'linear-gradient(135deg,#1a4d2e,#2e7d32)', color:'#fff',
          display:'flex', justifyContent:'space-between', alignItems:'center', py:1.5, px:2.5,
        }}>
          <Typography fontWeight={700} fontSize="0.9rem">Gestionar sesión</Typography>
          <IconButton size="small" onClick={() => setDlgGestion({ open:false, sesion:null })}
            sx={{ color:'#fff' }}>
            <CancelIcon fontSize="small"/>
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt:'20px !important' }}>
          {dlgGestion.sesion && (
            <Typography variant="body2" sx={{ color:'#555', mb:2 }}>
              Sesión con <strong>{dlgGestion.sesion.nombreAfiliado}</strong> —{' '}
              {formatFecha(dlgGestion.sesion.fechaSesion)}
            </Typography>
          )}

          <FormControl fullWidth size="small" sx={{ mb:2 }}>
            <InputLabel>Acción</InputLabel>
            <Select value={accion} label="Acción" onChange={e => setAccion(e.target.value)}>
              <MenuItem value="FINALIZAR">✅ Finalizar (marcar como realizada)</MenuItem>
              <MenuItem value="APLAZAR">📅 Aplazar (nueva fecha)</MenuItem>
              <MenuItem value="CANCELAR">❌ Cancelar</MenuItem>
            </Select>
          </FormControl>

          {accion === 'APLAZAR' && (
            <TextField fullWidth size="small" label="Nueva fecha y hora" type="datetime-local"
              value={nuevaFecha} onChange={e => setNuevaFecha(e.target.value)}
              InputLabelProps={{ shrink:true }} sx={{ mb:2 }}/>
          )}

          <TextField fullWidth size="small" multiline rows={3}
            label={accion === 'FINALIZAR' ? 'Notas de la sesión' : 'Motivo / observaciones'}
            placeholder={accion === 'FINALIZAR'
              ? 'Resumen de lo tratado en la sesión...'
              : accion === 'APLAZAR' ? 'Motivo del aplazamiento...' : 'Motivo de la cancelación...'}
            value={notas} onChange={e => setNotas(e.target.value)}/>

          {accion === 'CANCELAR' && (
            <Alert severity="warning" sx={{ mt:1.5, fontSize:'0.78rem' }}>
              Esta acción cancela la sesión definitivamente. El afiliado quedará sin sesión asignada.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px:2.5, py:1.5, borderTop:'1px solid #f0f0f0' }}>
          <Button onClick={() => setDlgGestion({ open:false, sesion:null })} sx={{ color:'#888' }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={confirmarGestion} disabled={guardando}
            startIcon={guardando ? <CircularProgress size={14} color="inherit"/> :
              accion === 'FINALIZAR' ? <CheckCircleIcon/> :
              accion === 'APLAZAR'  ? <EventIcon/> : <CancelIcon/>}
            color={accion === 'CANCELAR' ? 'error' : 'primary'}
            sx={accion !== 'CANCELAR' ? { background:'linear-gradient(135deg,#1a4d2e,#2e7d32)' } : {}}>
            {guardando ? 'Guardando...' :
              accion === 'FINALIZAR' ? 'Marcar realizada' :
              accion === 'APLAZAR'   ? 'Aplazar sesión' : 'Cancelar sesión'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
